import { jsx } from "panel";
import { ButtonEl } from "common/components/button";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { BillingFrequency, PricingCurveVariant } from "media/js/pricing/api/types";
import { toCurrency } from "media/js/pricing/plan-builder-2023/common/util";
import { TrackingEvents, TrackingPropertyNames } from "media/js/pricing/tracking";
import { RadioButtonEl } from "common/components/radio-button";
import marketingTooltipStyleOverride from "../common/marketing-tooltip.inline.styl";
import { annualDowngradeWarningBanner, banners } from "./banners";
const POSTDATED_PACKAGE_TOOLTIP_TEXT = `If you would like this add-on to start today, purchase it in a separate transaction prior to performing your future MTU downgrade.`;
export function template() {
  var _a, _b, _c, _d, _e, _f;
  const selectedPlanState = this.app.helpers.getSelectedPlanState();
  const currentPlanState = this.app.state.customerInfo.orgInfo.currentPlanState;
  const isAnnual = selectedPlanState.billingFrequency === BillingFrequency.ANNUAL;
  const disableMonthlyOnAnnual = currentPlanState.billingFrequency === BillingFrequency.ANNUAL;
  const disableMonthlyOnEnterprise = selectedPlanState.curveVariant === PricingCurveVariant.ENTERPRISE || currentPlanState.curveVariant === PricingCurveVariant.ENTERPRISE;
  const disableMonthly = disableMonthlyOnAnnual || disableMonthlyOnEnterprise;
  const isUnauthed = !((_a = this.state.customerInfo) == null ? void 0 : _a.isAuthenticated);
  const renderSkeletonOnBDLoading = (normalRender, keepMarginBottom) => this.app.state.isAmountDueInfoLoading ? /* @__PURE__ */ jsx("div", {
    class: { "no-margin": !keepMarginBottom },
    sel: ".mp-skeleton-medium.skeleton-breakdown"
  }) : normalRender;
  const renderFrequencyButton = () => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".freq-wrapper"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".freq-buttons"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".freq-radio",
      on: {
        click: () => {
          if (!disableMonthly) {
            this.app.helpers.handleChangeSelectedPlan({ billingFrequency: BillingFrequency.MONTHLY });
            this.app.helpers.trackEventWithPlanProps(TrackingEvents.BillingFrequencyChanged, {
              [TrackingPropertyNames.BillingFrequency]: `monthly`
            });
          }
        }
      },
      class: { disabled: disableMonthly }
    }, /* @__PURE__ */ jsx(RadioButtonEl, {
      className: "radio-button",
      selected: !isAnnual,
      disabled: disableMonthly
    }), disableMonthlyOnAnnual ? /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        placement: Placement.Top,
        "style-override": marketingTooltipStyleOverride
      }
    }, "Contact support to schedule a change to monthly billing when your plan renews.") : disableMonthlyOnEnterprise && /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        placement: Placement.Top,
        "style-override": marketingTooltipStyleOverride
      }
    }, "Enterprise plans are only available annually. Switch to Growth to enable monthly plans."), /* @__PURE__ */ jsx("div", {
      class: { "freq-label": true, disabled: disableMonthly }
    }, "Monthly")), /* @__PURE__ */ jsx("div", {
      sel: ".freq-radio",
      on: {
        click: () => {
          this.app.helpers.handleChangeSelectedPlan({ billingFrequency: BillingFrequency.ANNUAL });
          this.app.helpers.trackEventWithPlanProps(TrackingEvents.BillingFrequencyChanged, {
            [TrackingPropertyNames.BillingFrequency]: `annual`
          });
        }
      }
    }, /* @__PURE__ */ jsx(RadioButtonEl, {
      className: "radio-button",
      selected: isAnnual
    }), /* @__PURE__ */ jsx("div", {
      sel: ".freq-label"
    }, "Annual"))), this.helpers.showAnnualSaving() && /* @__PURE__ */ jsx("div", {
      sel: ".freq-savings"
    }, this.helpers.getAnnualSavingsCta()));
  };
  const renderPackageAddons = (packageId) => {
    const showDowngradeInfo = this.app.helpers.isReady() && this.app.helpers.isAnnualDowngrade() && this.app.helpers.isAddingPackage(packageId);
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-row"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-label"
    }, this.app.helpers.isReady() && this.helpers.getPackageName(packageId), showDowngradeInfo && /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-icon"
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: `info` }
    }), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        "max-width": `250px`,
        placement: Placement.Top,
        "style-override": marketingTooltipStyleOverride
      }
    }, POSTDATED_PACKAGE_TOOLTIP_TEXT))), /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-value"
    }, this.app.helpers.isReady() && toCurrency(this.app.helpers.getPackagePrice(packageId)))), showDowngradeInfo && /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-info.warn"
    }, `Starts ${this.app.helpers.getBillingPeriodString()}`));
  };
  const renderBreakdownList = (line) => {
    return /* @__PURE__ */ jsx("div", {
      class: { "breakdown-row": true, indent: line.indent }
    }, /* @__PURE__ */ jsx("div", {
      class: {
        "breakdown-label": true,
        detail: line.textIsGrey
      }
    }, line.description, line.infoText && /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-icon"
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: `info` }
    }), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        "max-width": `250px`,
        placement: Placement.Top,
        "style-override": marketingTooltipStyleOverride
      }
    }, line.infoText))), line.amount && /* @__PURE__ */ jsx("div", {
      class: { "breakdown-value": true, info: true, green: line.numberIsGreen }
    }, line.amount));
  };
  return /* @__PURE__ */ jsx("div", {
    sel: ".right-pane"
  }, ((_b = this.state.customerInfo) == null ? void 0 : _b.isAuthenticated) && banners(
    this.app.helpers.isAnnualDowngrade(),
    this.helpers.getValidBillingCredits(),
    this.app.state.customerInfo.orgInfo,
    this.attr(`mp-2023-style`)
  ), this.app.state.showCurrentPlanPane && /* @__PURE__ */ jsx("div", {
    sel: ".current-plan-breakdown-wrapper"
  }, this.attr(`mp-2023-style`) ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".current-plan-title"
  }, `Your Current Plan`), this.helpers.getCurrentPlanBreakdown().map((row) => {
    return /* @__PURE__ */ jsx(null, null, row.description && /* @__PURE__ */ jsx("div", {
      sel: ".current-plan-breakdown-row"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-label"
    }, row.description), /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-value"
    }, row.price)), row.dateString && /* @__PURE__ */ jsx("div", {
      sel: ".info"
    }, row.dateString));
  })) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-title"
  }, "Current Mixpanel Plan"), this.helpers.getCurrentPlanBreakdown().map((row) => {
    return /* @__PURE__ */ jsx(null, null, row.description && /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-row.current-plan-breakdown-row"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-label"
    }, row.description), /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-value.mp-font-size-large"
    }, row.price)), row.dateString && /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-row.current-plan-breakdown-row"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-label.info"
    }, row.dateString)));
  }))), /* @__PURE__ */ jsx("div", {
    sel: ".pricing-breakdown-wrapper"
  }, this.helpers.isOverMax() ? /* @__PURE__ */ jsx("div", {
    sel: ".over-25k",
    class: {
      "mp-font-size-large": !this.attr(`mp-2023-style`)
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".over-25k-title",
    class: {
      "mp-font-weight-bold": !this.attr(`mp-2023-style`)
    }
  }, this.app.helpers.isP2023SeptGootEnabled() ? `Contact sales for a bigger plan` : `Plans above ${this.app.helpers.abbreviateNumber(this.helpers.planMaxCap())} events`), /* @__PURE__ */ jsx("div", {
    sel: ".over-25k-text"
  }, "Our plan options don\u2019t stop at", ` `, this.app.helpers.abbreviateNumber(this.helpers.planMaxCap()), " events. Higher volume plans are just better served with a human touch and volume discounts. Get in touch for pricing that best serves your business goals."), /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      theme: this.attr(`mp-2023-style`) ? ButtonTheme.MarketingPrimary : ButtonTheme.Primary,
      href: `/contact-us/sales`,
      target: `_blank`,
      size: this.attr(`mp-2023-style`) ? ButtonSize.Large : ButtonSize.Medium
    },
    sel: ".contact-sales-btn",
    on: { click: this.helpers.trackContactSalesClicked }
  }, "Contact Sales")) : /* @__PURE__ */ jsx("div", {
    sel: ".pricing-breakdown"
  }, this.attr(`mp-2023-style`) && /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-title.org-title"
  }, ((_c = this.app.state.customerInfo.orgInfo) == null ? void 0 : _c.orgName) || `Organization`), this.attr(`mp-2023-style`) ? renderFrequencyButton() : /* @__PURE__ */ jsx("div", {
    sel: ".freq-wrap"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".freq-buttons"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".freq-radio",
    on: {
      click: () => {
        if (!disableMonthly) {
          this.app.helpers.handleChangeSelectedPlan({ billingFrequency: BillingFrequency.MONTHLY });
          this.app.helpers.trackEventWithPlanProps(TrackingEvents.BillingFrequencyChanged, {
            [TrackingPropertyNames.BillingFrequency]: `monthly`
          });
        }
      }
    },
    class: { disabled: disableMonthly }
  }, /* @__PURE__ */ jsx("div", {
    class: {
      selected: !isAnnual,
      disabled: disableMonthly,
      "freq-radio-btn-disable": disableMonthly,
      "freq-radio-btn": !disableMonthly
    }
  }), disableMonthlyOnAnnual ? /* @__PURE__ */ jsx(TooltipEl, {
    attrs: {
      placement: Placement.Top
    }
  }, "Contact support to schedule a change to monthly billing when your plan renews.") : disableMonthlyOnEnterprise && /* @__PURE__ */ jsx(TooltipEl, {
    attrs: {
      placement: Placement.Top
    }
  }, "Enterprise plans are only available annually. Switch to Growth to enable monthly plans."), /* @__PURE__ */ jsx("div", {
    class: { "freq-label-disable": disableMonthly, "freq-label": !disableMonthly },
    style: { "font-weight": isAnnual ? `normal` : `bold` }
  }, "Monthly")), /* @__PURE__ */ jsx("div", {
    sel: ".freq-radio",
    on: {
      click: () => {
        this.app.helpers.handleChangeSelectedPlan({ billingFrequency: BillingFrequency.ANNUAL });
        this.app.helpers.trackEventWithPlanProps(TrackingEvents.BillingFrequencyChanged, {
          [TrackingPropertyNames.BillingFrequency]: `annual`
        });
      }
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".freq-radio-btn",
    class: { selected: isAnnual }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".freq-label",
    style: { "font-weight": isAnnual ? `bold` : `normal` }
  }, "Annual"))), this.helpers.showAnnualSaving() && /* @__PURE__ */ jsx("div", {
    sel: ".freq-savings"
  }, this.helpers.getAnnualSavingsCta())), this.attr(`mp-2023-style`) ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-title"
  }, "Your Plan"), this.app.helpers.isReady() && /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-row"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-label"
  }, this.helpers.getCurveVariantLabel(), ` `, this.app.helpers.getSelectedCapString()), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-value"
  }, toCurrency(this.app.helpers.getSelectedPlanPrice()))), /* @__PURE__ */ jsx("div", {
    sel: ".annual-bucket-cap"
  }, isAnnual && this.app.helpers.getSelectedPlanState() && this.app.helpers.abbreviateNumber(this.app.helpers.getSelectedPlanState().cap * 12, 2) + ` events / year`)) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-divider"
  }), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-title"
  }, "Mixpanel Plan"), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-row"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-label.black"
  }, this.app.helpers.isReady() ? `${this.helpers.getCurveVariantLabel()} ${this.app.helpers.getSelectedCapString()}` : ``), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-value.mp-font-size-large"
  }, this.app.helpers.isReady() ? toCurrency(this.app.helpers.getSelectedPlanPrice()) : ``)), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-row"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".annual-bucket-cap"
  }, isAnnual && this.app.helpers.getSelectedPlanState() && this.app.helpers.abbreviateNumber(this.app.helpers.getSelectedPlanState().cap * 12, 2) + ` events / year`))), this.attr(`mp-2023-style`) ? this.app.helpers.isReady() && this.app.helpers.isAnnualDowngrade() && /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-info.warn"
  }, `Starts ${this.app.helpers.getBillingPeriodString()}`) : /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-row"
  }, this.app.helpers.isReady() && this.app.helpers.isAnnualDowngrade() && /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-label.info.warn"
  }, `Starts ${this.app.helpers.getBillingPeriodString()}`)), this.app.helpers.getSelectedPlanState().packageIds.length ? /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-title"
  }, "Add-Ons") : ``, this.app.helpers.getSelectedPlanState().packageIds.map(
    (packageId) => this.attr(`mp-2023-style`) ? renderPackageAddons(packageId) : /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-row.package-breakdown-row"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-label.black-flex"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-package-label-text"
    }, this.app.helpers.isReady() ? this.helpers.getPackageName(packageId) : ``), this.app.helpers.isReady() && this.app.helpers.isAnnualDowngrade() && this.app.helpers.isAddingPackage(packageId) && /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-icon"
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: `info` }
    }), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: { "max-width": `250px`, placement: Placement.Top }
    }, POSTDATED_PACKAGE_TOOLTIP_TEXT))), /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-value.mp-font-size-large"
    }, this.app.helpers.isReady() && toCurrency(this.app.helpers.getPackagePrice(packageId)))), /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-row"
    }, this.app.helpers.isReady() && this.app.helpers.isAnnualDowngrade() && this.app.helpers.isAddingPackage(packageId) && /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-label.info.warn"
    }, `Starts ${this.app.helpers.getBillingPeriodString()}`)))
  ), this.app.state.amountDueInfo && this.app.helpers.isReady() && (((_e = (_d = this.app.state.amountDueInfo) == null ? void 0 : _d.lines) == null ? void 0 : _e.length) || ((_f = this.app.state.amountDueInfo) == null ? void 0 : _f.amount)) ? /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-divider"
  }), /* @__PURE__ */ jsx("div", {
    sel: `${this.attr(`mp-2023-style`) ? `.breakdown-row` : `.breakdown-title`}.collapsible`,
    on: { click: this.app.helpers.toggleBreakdownExpanded }
  }, /* @__PURE__ */ jsx("div", null, "Breakdown"), /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: this.app.state.isBreakdownExpanded ? `caret-up` : `caret-down` }
  })), /* @__PURE__ */ jsx("div", {
    class: {
      "breakdown-collapsible": true,
      open: this.app.state.isBreakdownExpanded,
      close: !this.app.state.isBreakdownExpanded
    }
  }, this.app.state.isBreakdownExpanded && this.helpers.getBreakdownLines().map((line) => {
    return this.attr(`mp-2023-style`) ? renderBreakdownList(line) : /* @__PURE__ */ jsx("div", {
      class: { "breakdown-row": true, indent: line.indent }
    }, /* @__PURE__ */ jsx("div", {
      class: {
        "breakdown-title": true,
        "breakdown-label": true,
        detail: line.textIsGrey,
        "detail-black": !line.textIsGrey
      }
    }, line.description, line.infoText && /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-icon"
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: `info` }
    }), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: { "max-width": `250px`, placement: Placement.Top }
    }, line.infoText))), line.amount && /* @__PURE__ */ jsx("div", {
      class: { "breakdown-value": true, info: true, green: line.numberIsGreen }
    }, line.amount));
  }))) : /* @__PURE__ */ jsx("div", null), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-divider"
  }), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-row"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-title.breakdown-label.total"
  }, this.app.helpers.getTotalText()), this.app.state.amountDueInfo && this.state.amountDueInfo.amount !== null ? renderSkeletonOnBDLoading(
    /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-value.total-due.mp-font-size-large"
    }, toCurrency(this.app.state.amountDueInfo.amount)),
    true
  ) : isUnauthed ? renderSkeletonOnBDLoading(
    /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-value.total-due.mp-font-size-large"
    }, toCurrency(this.app.helpers.getTotalPrice())),
    true
  ) : renderSkeletonOnBDLoading(
    /* @__PURE__ */ jsx("div", {
      sel: ".breakdown-value.info.calculated-text"
    }, "Calculated at checkout"),
    true
  )), isUnauthed ? this.attr(`mp-2023-style`) ? /* @__PURE__ */ jsx("div", {
    sel: ".info-card"
  }, "Your first month will be prorated") : /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-row"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".mp-font-size-medium.info.breakdown-label"
  }, "Your first month will be prorated at checkout")) : /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-row"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".mp-font-size-large.info.breakdown-label",
    class: { bold: this.app.helpers.isAnnualDowngrade() }
  }, this.helpers.getStartOrRenewalInfo()), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-value.mp-font-size-large"
  }, this.app.helpers.isReady() && this.app.state.amountDueInfo ? toCurrency(this.app.helpers.getTotalPrice()) : ``)), this.app.helpers.getSalesTaxAppliedAmount() !== 0 && /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-row"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-title.breakdown-label"
  }), /* @__PURE__ */ jsx("div", {
    sel: ".breakdown-value.plus-tax-indicator"
  }, "(plus tax)")), this.attr(`mp-2023-style`) && annualDowngradeWarningBanner(this.app.helpers.isAnnualDowngrade()))), !this.attr(`mp-2023-style`) && /* @__PURE__ */ jsx("div", {
    sel: ".contact-support"
  }, "Need help?", ` `, /* @__PURE__ */ jsx("a", {
    attrs: { href: `https://mixpanel.com/get-support`, target: `_blank` },
    on: { click: this.helpers.trackContactSupportClicked }
  }, "Contact support")));
}
